import { useEffect, useMemo, useRef } from 'react';
import { List, ListItemButton, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import CasLink from 'src/compass/shared/components/CasLink/CasLink';
import Iconify from 'src/components/iconify';

// Direct paths, not the application barrel: this file is inside it, and importing it from itself
// is a cycle (see CasPendingEmailsPill in CasContextProviders).
import BatchRowListItem from './BatchRowListItem';
import { IBatchRow, IBatchTerms, RowChannelType } from './batchRow';
import type { BatchListViewType } from './useBatchListView';

// The pinned first entry.  A sentinel rather than a row key so it can never collide with one.
export const DEFAULT_KEY = ' default';

/*
  Sized to their captions and sharing what is left, rather than fullWidth's equal thirds: the list
  is 260px wide, and equal thirds wrapped the longest caption onto a second line. Tight padding here
  and around the group for the same reason - "All (958) | Invalid (123) | Skip (12)" fits on one
  line; only four-digit batches with three-digit Invalid and Skip counts still wrap.
*/
const VIEW_BUTTON_SX = { flexGrow: 1, px: 0.5, py: 0.25, textTransform: 'none' } as const;

interface Props {
  // Every row in the batch - the header counts these.
  rows: IBatchRow[];
  // The rows the current view shows - see useBatchListView.
  visibleRows: IBatchRow[];
  listView: BatchListViewType;
  onListViewChange: (view: BatchListViewType) => void;
  invalidCount: number;
  excludedCount: number;
  terms: IBatchTerms;
  included: Set<string>;
  getRowCustom: (key: string) => { recipients: boolean; message: boolean; channel: boolean };
  // Resolved by the dialog, which owns the per-row overrides it depends on.
  resolveRow: (row: IBatchRow) => RowChannelType;
  selectedKey: string;
  onSelect: (key: string) => void;
  onToggleInclude: (key: string) => void;
  // Applies to the keys given - the visible rows, so a filtered view's None takes out only those.
  onIncludeAll: (include: boolean, keys: string[]) => void;
}

export default function BatchRowList({
  rows,
  visibleRows,
  listView,
  onListViewChange,
  invalidCount,
  excludedCount,
  terms,
  included,
  getRowCustom,
  resolveRow,
  selectedKey,
  onSelect,
  onToggleInclude,
  onIncludeAll,
}: Props) {
  const listRef = useRef<HTMLUListElement>(null);

  // Switching views keeps the open row in sight - above all on the way back to All, where it would
  // otherwise sit somewhere in hundreds of rows. Centred rather than 'nearest', which parks it on
  // the list's bottom edge.
  useEffect(() => {
    listRef.current?.querySelector('.Mui-selected')?.scrollIntoView({ block: 'center' });
  }, [listView]);

  const visibleKeys = useMemo(() => new Set(visibleRows.map((row) => row.key)), [visibleRows]);

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant="subtitle2">
          Messages {included.size}/{rows.length}
        </Typography>
        {/* "Mark", because these check and uncheck rows - the toggle below is what filters them. */}
        <Stack direction="row" alignItems="baseline" spacing={1}>
          {/* Quieter than the links it introduces. */}
          <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
            Mark:
          </Typography>
          <CasLink onClick={() => onIncludeAll(true, Array.from(visibleKeys))}>All</CasLink>
          <CasLink onClick={() => onIncludeAll(false, Array.from(visibleKeys))}>None</CasLink>
        </Stack>
      </Stack>

      {/* Only when there is something to narrow to. Each option shows only while it has rows; an
          emptied view returns to All by itself (useBatchListView). */}
      {(invalidCount > 0 || excludedCount > 0) && (
        <Stack sx={{ px: 0.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
          <ToggleButtonGroup
            value={listView}
            exclusive
            size="small"
            sx={{ width: '100%' }}
            // Null when the selected option is clicked again - a view is always selected.
            onChange={(_, next: BatchListViewType | null) => next && onListViewChange(next)}
          >
            <ToggleButton value="all" sx={VIEW_BUTTON_SX}>
              All ({rows.length})
            </ToggleButton>
            {invalidCount > 0 && (
              <ToggleButton value="invalid" sx={{ ...VIEW_BUTTON_SX, color: 'error.main' }}>
                {/* The same warning the invalid rows carry in the list below. */}
                <Iconify
                  icon="eva:alert-triangle-fill"
                  width="14px"
                  color="#B71D18"
                  sx={{ mr: 0.25, flexShrink: 0 }}
                />
                Invalid ({invalidCount})
              </ToggleButton>
            )}
            {excludedCount > 0 && (
              <ToggleButton value="excluded" sx={VIEW_BUTTON_SX}>
                Skip ({excludedCount})
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </Stack>
      )}

      <List ref={listRef} disablePadding sx={{ overflowY: 'auto', flexGrow: 1 }}>
        <ListItemButton
          selected={selectedKey === DEFAULT_KEY}
          onClick={() => onSelect(DEFAULT_KEY)}
          sx={{ py: 1, px: 1, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Iconify icon="eva:star-fill" width="16px" height="16px" />
            <Typography variant="subtitle2">Default message</Typography>
          </Stack>
        </ListItemButton>

        {rows.length === 0 && (
          <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>
            {terms.emptyList}
          </Typography>
        )}

        {/* Derived here into primitives, and the handlers passed straight through, so each row can
            memoize on them - see BatchRowListItem.

            Every row stays mounted and a view hides the rest, rather than rendering only the visible
            ones: a batch lists hundreds, and remounting them on every switch back to All paid the
            dialog's opening cost over again. Hiding re-renders just the rows whose flag changed. */}
        {rows.map((row) => {
          const custom = getRowCustom(row.key);
          const { channel, reason } = resolveRow(row);

          return (
            <BatchRowListItem
              key={row.key}
              row={row}
              selected={selectedKey === row.key}
              included={included.has(row.key)}
              /*
                Recipients and channel are one label, not two. Pinning a channel re-addresses the
                row's contacts, so a row with edited recipients has had its channel settled as
                well - saying both would be saying the same thing twice. The narrower fact wins:
                "To/CC" when the sender chose the recipients, "Send as" when all they did was pick
                the channel.
              */
              customLabel={[
                custom.recipients ? 'To/CC' : (custom.channel && 'Send as') || null,
                custom.message && 'Msg',
              ]
                .filter(Boolean)
                .join(', ')}
              channel={channel}
              reason={reason}
              hidden={!visibleKeys.has(row.key)}
              onSelect={onSelect}
              onToggleInclude={onToggleInclude}
            />
          );
        })}
      </List>
    </>
  );
}
